import {
  ArrowBack,
  ArrowForward,
  CheckCircle,
  AccessTime,
  Flag,
  Check,
  Circle,
  Psychology,
  Speed
} from "components/ui/IconAdapter";
import {
  Button,
  Grid,
  Typography,
  LinearProgress,
  Card,
  CardContent,
  Box,
  Dialog,
  DialogContent,
  DialogActions,
  Container,
  AppBar,
  Toolbar,
  Chip,
  Avatar,
  CircularProgress
} from "components/ui/MaterialAdapter";
import { useDispatch, useSelector } from "react-redux";
import React, { useEffect, useState } from "react";
import {
  getAcademyExam,
  getAcademyExamByPrimaryId,
  getExamToStart,
  getExamByPrimaryId,
  saveExamProgress,
  startExam,
  updateUserCurriculum
} from "../../../api/action/exam";
import { getUserCurriculumByUserId } from "../../../api/action/products";
import CountdownTimer from "../LeftNav/Timer";
import { getAcademyExamByUserId } from "./../../../api/action/exam";
import QuestionNumbersBox from "./QuestionNumbersBox";
import { evaluateExam as evaluateExamAPI } from "../../../api/action/exam";
import { useLocation, useNavigate } from 'react-router-dom';
import RichTextDisplay from "../../../components/RichTextDisplay";
import {
  buildEducationGradePath,
  resolveEducationExamRouteContext,
} from '../../../domains/education/routes/examRouteContext';
import { stopExamStatusPolling } from "../../../domains/education/utils/examStatusPollingFlag";
import {
  firstExamResponse,
  getExamQuestionList,
  withExamQuestionList,
} from "../../../domains/education/services/examResponseUtils";
import { ensureCourseCertificate } from "../../../domains/common/certificates/certificateService";
import {
  getRequiredExamDefinitions,
  hasPassedRequiredExams,
} from "../../../domains/common/certificates/certificateEligibility";
import {
  applySelfPacedExamProgression,
  buildReadyToTakeExamPayload,
  isEvaluatedExam,
  isSelfPacedLearnerFlow,
} from './curriculumProgressionUtils';

const COLORS = {
  primary: 'var(--education-exam-primary, #746EAC)',
  secondary: 'var(--education-exam-accent, #3B9394)',
  success: 'var(--education-exam-success, #16A34A)',
  warning: 'var(--education-exam-warning, #F59E0B)',
  error: 'var(--education-exam-error, #DC2626)',
  background: 'var(--education-exam-background, #F8FAFC)',
  surface: 'var(--education-exam-surface, #FFFFFF)',
  surfaceElevated: 'var(--education-exam-surface, #FFFFFF)',
  textPrimary: 'var(--education-exam-text, #1E293B)',
  textSecondary: 'var(--education-exam-muted, #64748B)',
  border: 'var(--education-exam-border, #E2E8F0)',
  timerBg: 'var(--education-exam-primary, #1E293B)',
  timerText: '#FFFFFF'
};

const isCompletedCurriculum = (userCurriculum) =>
  [userCurriculum?.status, userCurriculum?.curriculum?.status].some((status) =>
    ['COMPLETE', 'COMPLETED'].includes(
      String(status || '').trim().replace(/\s+/g, '_').toUpperCase()
    )
  );

const getScheduledProductId = (course) => (
  course?.scheduledProduct?.primaryId
  || course?.scheduledProductId
  || course?.primaryId
);

const loadScheduledCertificateCompletion = async ({ company, course, dispatch, userId }) => {
  const deliveryType = course?.product?.deliveryType;
  const deliverySubType = course?.product?.deliverySubType;
  const scheduledProductId = getScheduledProductId(course);
  const productPrimaryId = course?.product?.primaryId
    || course?.scheduledProduct?.product?.primaryId;

  if (!company?.primaryId || !userId || !scheduledProductId) return null;

  const curriculumResponse = await dispatch(
    getUserCurriculumByUserId(
      userId,
      scheduledProductId,
      company.primaryId,
      deliveryType,
      deliverySubType,
      ['CHAPTER', 'EXAM'],
      scheduledProductId,
      company.parentCompanyId
    )
  );
  const curriculumRecord = Array.isArray(curriculumResponse)
    ? curriculumResponse[0]
    : curriculumResponse;
  const curriculum = curriculumRecord?.curriculum || curriculumRecord;

  if (!curriculum) return null;

  const requiredExamDefinitions = getRequiredExamDefinitions(curriculum);
  const examResults = requiredExamDefinitions.length > 0
    ? await getAcademyExam(
      company.primaryId,
      userId,
      null,
      scheduledProductId,
      deliveryType,
      deliverySubType,
      productPrimaryId
    )
    : [];
  const normalizedExamResults = Array.isArray(examResults) ? examResults : [];
  const requiredExamsPassed = hasPassedRequiredExams({
    curriculum,
    examResults: normalizedExamResults,
  });

  if (!isCompletedCurriculum(curriculumRecord) && !requiredExamsPassed) return null;

  return {
    completionDate: curriculum?.modifiedDate || curriculumRecord?.modifiedDate || new Date(),
    exams: normalizedExamResults,
  };
};

const readStoredJson = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key) || '{}');
  } catch {
    return {};
  }
};

const buildUserCurriculumUpdatePayload = ({
  userCurriculum,
  userPrimaryId,
  modifiedBy,
  modifiedDate,
}) => ({
  ...userCurriculum,
  primaryId: userCurriculum.primaryId,
  createdDate: userCurriculum.createdDate,
  createdBy: userCurriculum.createdBy,
  modifiedBy,
  modifiedDate,
  deleted: userCurriculum.deleted || false,
  user: {
    primaryId: userCurriculum.user?.primaryId || userPrimaryId,
    _id: userCurriculum.user?._id,
  },
  company: userCurriculum.company ? {
    primaryId: userCurriculum.company.primaryId,
    _id: userCurriculum.company._id,
  } : undefined,
  product: userCurriculum.product || null,
  curriculum: {
    ...userCurriculum.curriculum,
    primaryId: userCurriculum.curriculum?.primaryId,
    status: userCurriculum.curriculum?.status || 'IN_PROGRESS',
    createdDate: userCurriculum.curriculum?.createdDate,
    createdBy: userCurriculum.curriculum?.createdBy,
    modifiedBy,
    modifiedDate,
    chapterList: userCurriculum.curriculum?.chapterList?.map((chapter) => ({
      primaryId: chapter.primaryId,
      name: chapter.name,
      curriculumSerialNumber: chapter.curriculumSerialNumber,
      status: chapter.status,
      effectiveDate: chapter.effectiveDate,
      durationInHours: chapter.durationInHours,
      durationType: chapter.durationType,
      modifiedBy: chapter.modifiedBy || modifiedBy,
      modifiedDate: chapter.modifiedDate || modifiedDate,
      contentList: chapter.contentList?.map((content) => ({
        primaryId: content.primaryId,
        name: content.name,
        title: content.title,
        description: content.description,
        type: content.type,
        status: content.status,
        durationInHours: content.durationInHours,
        durationType: content.durationType,
        modifiedBy: content.modifiedBy || modifiedBy,
        modifiedDate: content.modifiedDate || modifiedDate,
      })) || [],
    })) || [],
    userExamsList: userCurriculum.curriculum?.userExamsList?.map((userExam) => ({
      primaryId: userExam.primaryId,
      curriculumSerialNumber: userExam.curriculumSerialNumber,
      status: userExam.status,
      chapterContentIndex: userExam.chapterContentIndex,
      effectiveDate: userExam.effectiveDate,
      modifiedBy: userExam.modifiedBy || modifiedBy,
      modifiedDate: userExam.modifiedDate || modifiedDate,
      exam: userExam.exam ? {
        primaryId: userExam.exam.primaryId,
        name: userExam.exam.name,
        duration: userExam.exam.duration,
        durationType: userExam.exam.durationType,
        subType: userExam.exam.subType,
      } : undefined,
    })) || [],
    examsList: [],
  },
});

const synchronizeStandaloneExam = async ({ nextItem, modifiedBy, now, timeZone }) => {
  if (nextItem?.type !== 'EXAM'
    || nextItem.item?.status !== 'ReadyToTake'
    || !nextItem.item?.primaryId) return;

  const academyExamResponse = await getAcademyExamByPrimaryId(nextItem.item.primaryId);
  const academyExam = firstExamResponse(academyExamResponse);
  if (!academyExam?.primaryId) {
    throw new Error('The next self-paced exam could not be loaded.');
  }

  const readyToTakePayload = buildReadyToTakeExamPayload({
    academyExam,
    modifiedBy,
    now,
    timeZone,
  });

  if (readyToTakePayload) {
    await saveExamProgress(readyToTakePayload);
  }
};

const Question = ({ initialExamData = null }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [examData, setExamData] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState([]);
  const [selectedAnswersMap, setSelectedAnswersMap] = useState({});
  const [markedQuestions, setMarkedQuestions] = useState([]);
  const [examType, setExamType] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [timeUpDialog, setTimeUpDialog] = useState(false);

  const companyData = useSelector((state) => state.companyReducer.company);
  const userData = useSelector((state) => state?.auth?.data);
  const userId = userData?.primaryId;
  const examRouteContext = resolveEducationExamRouteContext({
    search: location.search,
    state: location.state,
    storage: localStorage,
  });
  const examPrimaryId = examRouteContext.examId;
  const returnTo = examRouteContext.returnTo;
  const primaryId = userData?.primaryId;

  const progress = examData ? ((currentPage + 1) / examData.exam.questionList.length) * 100 : 0;
  const answeredCount = selectedAnswers.filter(answer => answer !== null).length;
  const markedCount = markedQuestions.length;
  const unansweredCount = examData ? examData.exam.questionList.length - answeredCount : 0;

  useEffect(() => {
    const loadSavedData = () => {
      try {
        const storedMap = localStorage.getItem("selectedAnswersMap");
        const storedMarked = localStorage.getItem("markedQuestions");

        if (storedMap) {
          const parsedMap = JSON.parse(storedMap);
          setSelectedAnswersMap(parsedMap);

          if (examData && examData.exam && examData.exam.questionList) {
            const answersArray = examData.exam.questionList.map(question => {
              const answerData = parsedMap[question.primaryId];
              return answerData ? Object.values(answerData.selectedAnswer)[0] : null;
            });
            setSelectedAnswers(answersArray);
          }
        }

        if (storedMarked) {
          setMarkedQuestions(JSON.parse(storedMarked));
        }
      } catch (error) {
        console.error("Error loading saved data:", error);
      }
    };

    if (examData) {
      loadSavedData();
    }
  }, [examData]);

  useEffect(() => {
    const fetchUserExamData = async () => {
      try {
        if (!userId) {
          console.error("User ID is missing.");
          return;
        }

        const isEducationDomain = localStorage.getItem('domainType') === 'EDUCATION';
        if (!examPrimaryId && isEducationDomain) {
          navigate(returnTo, { replace: true });
          return;
        }

        const examResponse = initialExamData || (examPrimaryId
          ? await getExamToStart(examPrimaryId)
          : await getAcademyExamByUserId(userId));

        if (!examResponse || examResponse.length === 0) {
          console.error("Exam data is empty or invalid.");
          return;
        }

        const examData = firstExamResponse(examResponse);
        if (!examData.exam) {
          console.error("Exam data is missing in response.");
          return;
        }

        setExamType(examData.exam.type);
        localStorage.setItem("examDuration", examData.exam.type);
        localStorage.setItem("primaryId", examData.primaryId);

        const response = await startExam(examData.primaryId);

        // startExam should return the exam with its questionList. If it comes back
        // without questions, fall back to the getExamToStart payload which carries them.
        let resolvedExam = firstExamResponse(response);
        let resolvedQuestionList = getExamQuestionList(resolvedExam);

        // The user-exam endpoint may return an embedded exam reference with an
        // empty questionList, even though the canonical exam record has them.
        // Load that canonical record before showing the empty-state message.
        if (resolvedQuestionList.length === 0) {
          const fullExam = await getExamByPrimaryId(examData.exam.primaryId);
          resolvedQuestionList = getExamQuestionList(fullExam);
        }

        resolvedExam = withExamQuestionList(resolvedExam || examData, resolvedQuestionList);

        setExamData(resolvedExam);

        const storedMap = localStorage.getItem("selectedAnswersMap");
        if (storedMap) {
          const parsedMap = JSON.parse(storedMap);
          setSelectedAnswersMap(parsedMap);

          const answersArray = resolvedQuestionList.map(question => {
            const answerData = parsedMap[question.primaryId];
            return answerData ? Object.values(answerData.selectedAnswer)[0] : null;
          });
          setSelectedAnswers(answersArray);
        } else {
          const emptyAnswers = new Array(resolvedQuestionList.length).fill(null);
          setSelectedAnswers(emptyAnswers);
        }
      } catch (error) {
        console.error("Error fetching exam data:", error);
      }
    };

    fetchUserExamData();
  }, [userId, examPrimaryId, navigate, returnTo, initialExamData]);

  useEffect(() => {
    if (examData && selectedAnswers[currentPage] !== undefined) {
      setSelectedOption(selectedAnswers[currentPage]);
    }
  }, [currentPage, examData, selectedAnswers]);

  const handleNextPage = async () => {
    await saveOption(selectedAnswersMap);
    setCurrentPage(prev => Math.min(prev + 1, examData.exam.questionList.length - 1));
  };

  const handleInterviewNextPage = async () => {
    await saveProgress(selectedAnswersMap, "In_Progress");
    setCurrentPage(prev => Math.min(prev + 1, examData.exam.questionList.length - 1));
  };

  const handlePrevPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 0));
  };

  const handleQuestionNumberClick = (index) => {
    setCurrentPage(index);
  };

  const handleOptionChange = (event) => {
    const { value } = event.target;
    setSelectedOption(value);

    const question = examData.exam.questionList[currentPage];
    if (!question) return;

    let selectedOptionIndex;

    if (question.answersOptionsMap) {
      selectedOptionIndex = Object.keys(question.answersOptionsMap).find(
        (key) => question.answersOptionsMap[key] === value
      );
    } else if (question.answerOptionsList) {
      selectedOptionIndex = question.answerOptionsList.indexOf(value);
    }

    const updatedMap = { ...selectedAnswersMap };
    updatedMap[question.primaryId] = {
      selectedAnswer: { [selectedOptionIndex]: value },
      questionId: question.primaryId
    };
    setSelectedAnswersMap(updatedMap);

    const updatedSelectedAnswers = [...selectedAnswers];
    updatedSelectedAnswers[currentPage] = value;
    setSelectedAnswers(updatedSelectedAnswers);

    localStorage.setItem("selectedAnswersMap", JSON.stringify(updatedMap));
  };

  const buildSelectedAnswersForQuestion = (question, answersByQuestion = selectedAnswersMap) => {
    const answerData = answersByQuestion[question.primaryId];
    return answerData?.selectedAnswer || {};
  };

  const buildCurrentAttemptForQuestion = (question, exam, answersByQuestion = selectedAnswersMap) => ({
    attemptNumber: Math.max(1, Number(exam?.totalNoOfTimesAttempted) || 1),
    selectedAnswersMap: buildSelectedAnswersForQuestion(question, answersByQuestion)
  });

  const handleNextOrSubmit = () => {
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const handleTimeUp = async () => {
    setTimeUpDialog(true);
    setTimeout(() => {
      handleSubmitExam();
    }, 5000);
  };

  const markForReview = () => {
    const updatedMarkedQuestions = markedQuestions.includes(currentPage)
      ? markedQuestions.filter(q => q !== currentPage)
      : [...markedQuestions, currentPage];

    setMarkedQuestions(updatedMarkedQuestions);
    localStorage.setItem("markedQuestions", JSON.stringify(updatedMarkedQuestions));
  };

  const getAllOptions = (question) => {
    const options = [];
    const optionLabels = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

    if (!question) {
      return options;
    }

    if (question.answerOptionsList) {
      question.answerOptionsList.forEach((option, index) => {
        options.push({
          value: option,
          label: option,
          index: index,
          optionLabel: optionLabels[index] || (index + 1).toString()
        });
      });
    }

    if (question.answersOptionsMap) {
      Object.entries(question.answersOptionsMap).forEach(([key, value], index) => {
        options.push({
          value: value,
          label: value,
          index: options.length,
          optionLabel: optionLabels[index] || (index + 1).toString()
        });
      });
    }

    return options;
  };

  const handleEvaluateExam = async (examDataToEvaluate) => {
    try {
      const CompanyId = companyData.primaryId;

      const evaluationPayload = {
        primaryId: examDataToEvaluate.primaryId,
        dynamicAttributes: examDataToEvaluate.dynamicAttributes,
        deleted: false,
        totalNoOfTimesAttempted: examDataToEvaluate.totalNoOfTimesAttempted,
        grade: examDataToEvaluate.grade,
        status: "Completed",
        modifiedBy: "userId",
        user: {
          userId: userId,
          primaryId: primaryId,
        },
        exam: {
          primaryId: examDataToEvaluate.exam.primaryId,
          name: examDataToEvaluate.exam.name,
          type: examDataToEvaluate.exam.type,
          subType: examDataToEvaluate.exam.subType,
          questionList: examDataToEvaluate.exam.questionList.map((question) => {
            const answerData = selectedAnswersMap[question.primaryId];

            const cleanQuestion = {
              primaryId: question.primaryId,
              question: question.question,
              answerOptionsList: question.answerOptionsList,
              answersOptionsMap: question.answersOptionsMap,
              correctAnswer: question.correctAnswer,
              currentAttempt: buildCurrentAttemptForQuestion(question, examDataToEvaluate),
              selectedAnswersMap: answerData ? {
                selectedAnswer: JSON.stringify(answerData.selectedAnswer),
                questionId: answerData.questionId
              } : {
                selectedAnswer: "",
                questionId: question.primaryId
              }
            };

            return cleanQuestion;
          }),
        },
        scheduledProduct: examDataToEvaluate.scheduledProduct,
        schedule: examDataToEvaluate.schedule,
        videoRecording: examDataToEvaluate.videoRecording,
        _id: examDataToEvaluate._id,
        company: {
          // code: CompCode,
          // primaryId: CompanyId,
          primaryId: CompanyId,
        },
      };

      const response = await evaluateExamAPI(evaluationPayload);
      return response;
    } catch (error) {
      console.error("Error evaluating exam:", error);
      throw error;
    }
  };

  const handleSubmitExam = async () => {
  if (isSubmitting) return;

  setIsSubmitting(true);
  setOpenDialog(false);
  setTimeUpDialog(false);

  try {
    await saveProgress(selectedAnswersMap, "Completed");
    const evaluationResult = await handleEvaluateExam(examData);

    const selectedCompanyDomain = localStorage.getItem("domainType");
    const currentCourse = readStoredJson("currentCourse");

    let courseName = currentCourse?.name || examData.exam?.name || 'course';

    if (courseName.includes('/')) {
      courseName = courseName.split('/')[0];
    }

    if (courseName.includes('%2F')) {
      courseName = courseName.split('%2F')[0];
    }

    if (selectedCompanyDomain === "EDUCATION" && isEvaluatedExam(evaluationResult)) {
      const isSelfPaced = isSelfPacedLearnerFlow({ currentCourse, examData });

      if (!isSelfPaced) {
        try {
          const completion = await loadScheduledCertificateCompletion({
            company: companyData,
            course: currentCourse,
            dispatch,
            userId,
          });

          if (completion) {
            await ensureCourseCertificate({
              company: companyData,
              completionDate: completion.completionDate,
              course: currentCourse,
              dispatch,
              evaluationResult,
              exams: completion.exams,
              sendNotification: true,
              user: userData,
            });
          }
        } catch (certificateError) {
          // Exam completion is already persisted. Certificate/notification failures
          // must not prevent the learner from seeing their grade or continuing.
          console.error("Certificate issuance after scheduled exam completion failed:", certificateError);
        }
      }

      if (isSelfPaced) {
      const storedUserCurriculum = readStoredJson("curriculum");
      if (!storedUserCurriculum.primaryId || !storedUserCurriculum.curriculum?.primaryId) {
        throw new Error("The self-paced curriculum is missing from the current exam session.");
      }

      const now = new Date();
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const modifiedBy = [userData?.firstName, userData?.lastName].filter(Boolean).join(' ') || 'SYSTEM';
      const progression = applySelfPacedExamProgression({
        userCurriculum: storedUserCurriculum,
        completedExamId: examData.primaryId,
        completedSerial: examData.curriculumSerialNumber,
        evaluationResult,
        modifiedBy,
        now,
        timeZone,
      });

      if (!progression.currentExam) {
        throw new Error("The evaluated exam was not found in the self-paced curriculum.");
      }

      if (progression.passed && progression.nextItem?.type === 'EXAM') {
        await synchronizeStandaloneExam({
          nextItem: progression.nextItem,
          modifiedBy,
          now,
          timeZone,
        });
      }

      const modifiedDate = now.toISOString();
      await updateUserCurriculum(buildUserCurriculumUpdatePayload({
        userCurriculum: progression.userCurriculum,
        userPrimaryId: userId,
        modifiedBy,
        modifiedDate,
      }));
      localStorage.setItem("curriculum", JSON.stringify(progression.userCurriculum));

      if (isCompletedCurriculum(progression.userCurriculum)) {
        try {
          await ensureCourseCertificate({
            company: companyData,
            completionDate: now,
            course: currentCourse,
            dispatch,
            evaluationResult,
            sendNotification: true,
            user: userData,
          });
        } catch (certificateError) {
          // Exam completion is already persisted. Certificate/notification failures
          // must not prevent the learner from seeing their grade or continuing.
          console.error("Certificate issuance after exam completion failed:", certificateError);
        }
      }
      }
    }

    setSelectedAnswersMap({});
    setMarkedQuestions([]);
    localStorage.removeItem("selectedAnswersMap");
    localStorage.removeItem("markedQuestions");

    const gradeRoute = buildEducationGradePath(courseName, examData.primaryId);

    if (document.fullscreenElement) {
      document.exitFullscreen().then(() => {
        navigate(gradeRoute);
      }).catch((err) => {
        navigate(gradeRoute);
      });
    } else {
      navigate(gradeRoute);
    }

  } catch (error) {
    console.error("Error submitting exam or updating exam progression:", error);
    const currentCourse = readStoredJson("currentCourse");
    let courseName = currentCourse?.name || examData.exam?.name || 'course';

    if (courseName.includes('/')) courseName = courseName.split('/')[0];
    if (courseName.includes('%2F')) courseName = courseName.split('%2F')[0];

    const gradeRoute = buildEducationGradePath(courseName, examData.primaryId);
    navigate(gradeRoute);

  } finally {
    stopExamStatusPolling();
    setIsSubmitting(false);
  }
};

  const saveProgress = async (selectedAnswersMap, status) => {
    const CompanyId = companyData.primaryId;

    const examProgress = {
      primaryId: examData.primaryId,
      dynamicAttributes: examData.dynamicAttributes,
      deleted: false,
      totalNoOfTimesAttempted: examData.totalNoOfTimesAttempted,
      curriculumSerialNumber: examData.curriculumSerialNumber,
      chapterContentIndex: examData.chapterContentIndex,
      grade: examData.grade,
      status: status,
      modifiedBy: "userId",
      user: { userId: userId, primaryId: primaryId },
      exam: {
        ...examData.exam,
        primaryId: examData.exam.primaryId,
        effectiveDate: {
          effectiveEndDate: new Date().toISOString()
        },
        questionList: examData.exam.questionList.map((question) => {
          const answerData = selectedAnswersMap[question.primaryId];
          return {
            ...question,
            currentAttempt: buildCurrentAttemptForQuestion(question, examData, selectedAnswersMap),
            selectedAnswersMap: answerData ? {
              selectedAnswer: JSON.stringify(answerData.selectedAnswer),
              questionId: answerData.questionId
            } : { selectedAnswer: "", questionId: question.primaryId }
          };
        }),
      },
      scheduledProduct: examData.scheduledProduct,
      schedule: examData.schedule,
      videoRecording: examData.videoRecording,
      _id: examData._id,
      company: {  primaryId: CompanyId},
    };

    try {
      await saveExamProgress(examProgress);
    } catch (error) {
      console.error("Error saving exam progress:", error);
    }
  };

  const saveOption = async (selectedAnswersMap) => {
    await saveProgress(selectedAnswersMap, "In_Progress");
  };

  if (!examData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" sx={{ backgroundColor: COLORS.background }}>
        <Box textAlign="center">
          <Typography variant="h6" gutterBottom sx={{ color: COLORS.textPrimary, mb: 2 }}>
            Loading Exam
          </Typography>
          <LinearProgress
            sx={{
              width: 200,
              mx: 'auto',
              height: 6,
              borderRadius: 3,
              backgroundColor: COLORS.border,
              '& .MuiLinearProgress-bar': {
                backgroundColor: COLORS.primary,
                borderRadius: 3
              }
            }}
          />
        </Box>
      </Box>
    );
  }

  const questionList = examData.exam.questionList || [];

  if (questionList.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" sx={{ backgroundColor: COLORS.background }}>
        <Box textAlign="center" sx={{ px: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ color: COLORS.textPrimary, mb: 1 }}>
            No questions available for this exam
          </Typography>
          <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>
            Please contact your administrator or try again later.
          </Typography>
        </Box>
      </Box>
    );
  }

  const safeCurrentPage = Math.min(Math.max(currentPage, 0), questionList.length - 1);
  const currentQuestion = questionList[safeCurrentPage];
  const options = getAllOptions(currentQuestion);
  const isMarked = markedQuestions.includes(currentPage);

  const SubmitConfirmationDialog = () => (
    <Dialog
      id="submit-exam-dialog"
      open={openDialog}
      onClose={handleDialogClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          boxShadow: '0 32px 96px rgba(0,0,0,0.25)',
          background: 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)',
          overflow: 'hidden'
        }
      }}
    >
      <Box
        sx={{
          background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%)`,
          py: 4,
          px: 4,
          textAlign: 'center',
          color: 'white'
        }}
      >

        <Typography variant="h4" fontWeight={800} sx={{ mb: 1 }}>
          Ready to Submit?
        </Typography>
        <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 500 }}>
          Review your exam progress before final submission
        </Typography>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 4, borderBottom: `1px solid ${COLORS.border}` }}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" fontWeight={700} sx={{ color: COLORS.textPrimary, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Psychology sx={{ color: COLORS.primary }} />
                Exam Summary
              </Typography>

              <Box sx={{ space: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5 }}>
                  <Typography variant="body1" sx={{ color: COLORS.textSecondary, fontWeight: 500 }}>
                    Total Questions
                  </Typography>
                  <Chip
                    id="total-questions-count"
                    label={examData.exam.questionList.length}
                    sx={{
                      backgroundColor: COLORS.primary,
                      color: 'white',
                      fontWeight: 700,
                      minWidth: 60
                    }}
                  />
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5 }}>
                  <Typography variant="body1" sx={{ color: COLORS.textSecondary, fontWeight: 500 }}>
                    Answered
                  </Typography>
                  <Chip
                    id="answered-count"
                    label={answeredCount}
                    sx={{
                      backgroundColor: COLORS.success,
                      color: 'white',
                      fontWeight: 700,
                      minWidth: 60
                    }}
                  />
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5 }}>
                  <Typography variant="body1" sx={{ color: COLORS.textSecondary, fontWeight: 500 }}>
                    Marked for Review
                  </Typography>
                  <Chip
                    id="marked-count"
                    label={markedCount}
                    sx={{
                      backgroundColor: COLORS.warning,
                      color: 'white',
                      fontWeight: 700,
                      minWidth: 60
                    }}
                  />
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5 }}>
                  <Typography variant="body1" sx={{ color: COLORS.textSecondary, fontWeight: 500 }}>
                    Not Answered
                  </Typography>
                  <Chip
                    id="unanswered-count"
                    label={unansweredCount}
                    sx={{
                      backgroundColor: unansweredCount > 0 ? COLORS.error : COLORS.success,
                      color: 'white',
                      fontWeight: 700,
                      minWidth: 60
                    }}
                  />
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="h6" fontWeight={700} sx={{ color: COLORS.textPrimary, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Speed sx={{ color: COLORS.primary }} />
                Completion Progress
              </Typography>

              <Box sx={{ position: 'relative', display: 'inline-flex', width: '100%', justifyContent: 'center' }}>
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                  <CircularProgress
                    variant="determinate"
                    value={100}
                    size={160}
                    thickness={4}
                    sx={{ color: COLORS.border, position: 'absolute' }}
                  />
                  <CircularProgress
                    variant="determinate"
                    value={(answeredCount / examData.exam.questionList.length) * 100}
                    size={160}
                    thickness={4}
                    sx={{
                      color: COLORS.primary,
                      filter: 'drop-shadow(0 4px 12px rgba(116, 110, 172, 0.4))'
                    }}
                  />
                  <Box
                    sx={{
                      top: 0,
                      left: 0,
                      bottom: 0,
                      right: 0,
                      position: 'absolute',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexDirection: 'column'
                    }}
                  >
                    <Typography variant="h4" component="div" fontWeight={800} sx={{ color: COLORS.primary }}>
                      {Math.round((answeredCount / examData.exam.questionList.length) * 100)}%
                    </Typography>
                    <Typography variant="caption" sx={{ color: COLORS.textSecondary, fontWeight: 600 }}>
                      Complete
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ mt: 4 }}>
                <LinearProgress
                  id="progress-bar"
                  variant="determinate"
                  value={(answeredCount / examData.exam.questionList.length) * 100}
                  sx={{
                    height: 12,
                    borderRadius: 6,
                    backgroundColor: COLORS.border,
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: COLORS.primary,
                      borderRadius: 6,
                      background: `linear-gradient(90deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%)`
                    }
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5 }}>
                  <Typography variant="caption" sx={{ color: COLORS.textSecondary, fontWeight: 600 }}>
                    Progress: {answeredCount}/{examData.exam.questionList.length}
                  </Typography>
                  <Typography variant="caption" sx={{ color: COLORS.textSecondary, fontWeight: 600 }}>
                    {unansweredCount} questions remaining
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ p: 3, backgroundColor: `${COLORS.warning}08`, borderLeft: `4px solid ${COLORS.warning}`, mx: 4, my: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <AccessTime sx={{ color: COLORS.warning, fontSize: 24, mt: 0.25 }} />
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 700, color: COLORS.textPrimary, mb: 0.5 }}>
                Important Notice
              </Typography>
              <Typography variant="body2" sx={{ color: COLORS.textSecondary, lineHeight: 1.6 }}>
                Once you submit your exam, you cannot make any changes. Please ensure you have reviewed all your answers before proceeding.
                {unansweredCount > 0 && (
                  <strong style={{ color: COLORS.error }}>
                    {" "}You have {unansweredCount} unanswered question{unansweredCount > 1 ? 's' : ''}.
                  </strong>
                )}
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 4, gap: 2, borderTop: `1px solid ${COLORS.border}` }}>
        <Button
          data-testid="continue-exam-btn"
          onClick={handleDialogClose}
          variant="outlined"
          fullWidth
          size="large"
          sx={{
            fontWeight: 700,
            py: 1.5,
            borderColor: COLORS.border,
            color: COLORS.textPrimary,
            borderRadius: 3,
            fontSize: '1rem',
            textTransform: 'none',
            '&:hover': {
              borderColor: COLORS.primary,
              backgroundColor: `${COLORS.primary}08`,
              transform: 'translateY(-2px)',
              boxShadow: `0 8px 25px rgba(116, 110, 172, 0.2)`
            },
            transition: 'all 0.3s ease'
          }}
        >
          Continue Exam
        </Button>

        <Button
          data-testid="confirm-submit-exam"
          fullWidth
          variant="contained"
          onClick={handleSubmitExam}
          disabled={isSubmitting}
          size="large"
          sx={{
            py: 1.8,
            fontWeight: 700,
            fontSize: '1rem',
            backgroundColor: COLORS.secondary,
            borderRadius: 3,
            textTransform: 'none',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 12px 35px rgba(59, 147, 148, 0.4)`,
              backgroundColor: COLORS.secondary
            },
            transition: 'all 0.3s ease',
            boxShadow: `0 6px 20px rgba(59, 147, 148, 0.3)`
          }}
        >
          {isSubmitting ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={22} sx={{ color: 'white' }} />
              Submitting Exam...
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <CheckCircle sx={{ fontSize: 22 }} />
              Confirm & Submit Exam
            </Box>
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <>
      <Box sx={{ minHeight: '100vh', backgroundColor: COLORS.background }}>
        <AppBar
          position="static"
          elevation={0}
          sx={{
            backgroundColor: COLORS.background,
            borderBottom: `1px solid ${COLORS.border}`,
            background: `linear-gradient(135deg, ${COLORS.background} 0%, #FFFFFF 100%)`
          }}
        >
          <Toolbar sx={{
            py: 3,
            px: 4
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                sx={{
                  backgroundColor: COLORS.primary,
                  width: 44,
                  height: 44,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  boxShadow: '0 4px 12px rgba(116, 110, 172, 0.3)'
                }}
              >
                {examData.exam.name.charAt(0)}
              </Avatar>
              <Box>
                <Typography variant="h5" sx={{ color: COLORS.textPrimary, fontWeight: 700 }}>
                  {examData.exam.name}
                </Typography>
                <Typography variant="body2" sx={{ color: COLORS.textSecondary, fontSize: '0.9rem' }}>
                  Exam
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, ml: 'auto' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: COLORS.timerBg,
                    px: 3,
                    py: 1.5,
                    borderRadius: 3,
                    border: `2px solid ${COLORS.primary}`,
                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                    minWidth: '140px',
                    justifyContent: 'center',
                    gap: 1.5
                  }}
                >
                  <AccessTime sx={{ color: COLORS.timerText, fontSize: 20 }} />
                  <CountdownTimer
                    exam={examData}
                    initialTime={3600}
                    onTimeUp={handleTimeUp}
                    sx={{
                      color: COLORS.timerText,
                      fontWeight: 800,
                      fontSize: '1.3rem',
                      fontFamily: "'Courier New', monospace",
                      letterSpacing: '1px'
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 3,
                backgroundColor: COLORS.surface,
                px: 3,
                py: 2,
                borderRadius: 3,
                border: `1px solid ${COLORS.border}`,
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
              }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ color: COLORS.textSecondary, fontSize: '0.75rem', fontWeight: 600, mb: 0.5 }}>
                    Ques
                  </Typography>
                  <Typography variant="h6" sx={{ color: COLORS.primary, fontWeight: 800 }}>
                    {currentPage + 1}<span style={{ color: COLORS.textSecondary, fontWeight: 500 }}>/{examData.exam.questionList.length}</span>
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Toolbar>

          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 6,
              backgroundColor: 'transparent',
              '& .MuiLinearProgress-bar': {
                backgroundColor: COLORS.primary,
                borderRadius: 3
              }
            }}
          />
        </AppBar>

        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <Card
                elevation={0}
                sx={{
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 3,
                  backgroundColor: COLORS.surfaceElevated,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                  background: 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)'
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: COLORS.textPrimary,
                      fontWeight: 700,
                      mb: 3,
                      fontSize: '1.1rem'
                    }}
                  >
                    Question navigator
                  </Typography>

                  <QuestionNumbersBox
                    questionList={examData.exam.questionList}
                    currentPage={currentPage}
                    selectedAnswersList={selectedAnswers}
                    markedQuestions={markedQuestions}
                    handleQuestionNumberClick={handleQuestionNumberClick}
                    colors={COLORS}
                  />

                  <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Button
                      data-testid="submit-exam-btn"
                      onClick={handleNextOrSubmit}
                      variant="contained"
                      fullWidth
                      disabled={isSubmitting}
                      sx={{
                        fontWeight: 600,
                        py: 1.5,
                        backgroundColor: COLORS.secondary,
                        borderRadius: 2,
                        textTransform: 'none',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: `0 8px 25px rgba(59, 147, 148, 0.4)`,
                          backgroundColor: COLORS.secondary
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {isSubmitting ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={20} sx={{ color: 'white' }} />
                          Submitting...
                        </Box>
                      ) : (
                        "Submit Exam"
                      )}
                    </Button>

                    <Button
                      data-testid="mark-review-btn"
                      fullWidth
                      variant="outlined"
                      startIcon={<Flag />}
                      onClick={markForReview}
                      sx={{
                        py: 1.8,
                        fontWeight: 500,
                        borderColor: isMarked ? COLORS.warning : COLORS.primary,
                        color: isMarked ? COLORS.warning : COLORS.primary,
                        backgroundColor: isMarked ? `${COLORS.warning}08` : 'transparent',
                        borderRadius: 2,
                        textTransform: 'none',
                        '&:hover': {
                          borderColor: COLORS.primary,
                          backgroundColor: `${COLORS.primary}08`,
                          transform: 'translateY(-1px)'
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {isMarked ? "Unmark review" : "Mark for review"}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card
                elevation={0}
                sx={{
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 3,
                  backgroundColor: COLORS.surfaceElevated,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                  background: 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)'
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ mb: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Chip
                          data-testid="current-question-number"
                          label={`Question ${currentPage + 1}`}
                          sx={{
                            backgroundColor: COLORS.primary,
                            color: 'white',
                            fontWeight: 700,
                            fontSize: '0.9rem',
                            height: 32,
                            boxShadow: '0 2px 8px rgba(116, 110, 172, 0.3)'
                          }}
                        />

                        {isMarked && (
                          <Chip
                            data-testid="marked-review-chip"
                            icon={<Flag />}
                            label="Marked for review"
                            size="small"
                            sx={{
                              backgroundColor: COLORS.warning,
                              color: 'white',
                              fontWeight: 600,
                              height: 32
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>

<Box id="question-text" sx={{ mb: 4 }}> 
                    <RichTextDisplay content={currentQuestion.question} />
                  </Box>

                  <Box sx={{ mb: 4 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: COLORS.textPrimary,
                        mb: 3,
                        fontSize: '1.1rem'
                      }}
                    >
                      Select your answer:
                    </Typography>

                    <Box id={`question-${currentPage + 1}`} data-current="true" sx={{ space: 2 }}>
                      {options.map((option, index) => {
                        const isSelected = selectedOption === option.value;
                        const optionLetter = option.optionLabel;
                        const testId = `option-${optionLetter}`;
                        
                        return (
                          <Box
                            key={index}
                            data-testid={testId}
                            id={testId}
                            aria-checked={isSelected}
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              p: 3,
                              mb: 2,
                              border: isSelected ? `2px solid ${COLORS.primary}` : `1px solid ${COLORS.border}`,
                              borderRadius: 2,
                              backgroundColor: isSelected ? `${COLORS.primary}08` : COLORS.surface,
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                borderColor: COLORS.primary,
                                backgroundColor: `${COLORS.primary}08`,
                                transform: 'translateY(-2px)',
                                boxShadow: '0 8px 20px rgba(0,0,0,0.1)'
                              }
                            }}
                            onClick={() => handleOptionChange({ target: { value: option.value } })}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 36,
                                height: 36,
                                borderRadius: '50%',
                                border: isSelected ? `2px solid ${COLORS.primary}` : `2px solid ${COLORS.border}`,
                                backgroundColor: isSelected ? COLORS.primary : 'transparent',
                                mr: 3,
                                flexShrink: 0,
                                transition: 'all 0.3s ease',
                                fontWeight: 700,
                                fontSize: '1rem',
                                color: isSelected ? 'white' : COLORS.textSecondary,
                                boxShadow: isSelected ? '0 4px 12px rgba(116, 110, 172, 0.4)' : 'none'
                              }}
                            >
                              {optionLetter}
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: isSelected ? 600 : 400,
                                  color: isSelected ? COLORS.primary : COLORS.textPrimary,
                                  lineHeight: 1.6,
                                  fontSize: '1rem'
                                }}
                              >
                                {option.label}
                              </Typography>
                            </Box>
                            {isSelected && (
                              <Check sx={{ color: COLORS.primary, ml: 2, fontSize: 24 }} />
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>

                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pt: 3,
                    borderTop: `1px solid ${COLORS.border}`,
                    mt: 4
                  }}>
                    <Button
                      data-testid="prev-question-btn"
                      onClick={handlePrevPage}
                      startIcon={<ArrowBack />}
                      disabled={currentPage === 0 || isSubmitting}
                      sx={{
                        fontWeight: 600,
                        color: currentPage === 0 ? COLORS.textSecondary : COLORS.primary,
                        borderRadius: 2,
                        px: 3,
                        py: 1,
                        textTransform: 'none',
                        '&:hover': {
                          backgroundColor: `${COLORS.primary}08`,
                          transform: 'translateY(-1px)'
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Previous
                    </Button>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                      {currentPage < examData.exam.questionList.length - 1 && (
                        <Button
                          data-testid="next-question-btn"
                          onClick={examType === "Academy" ? handleNextPage : handleInterviewNextPage}
                          variant="contained"
                          endIcon={<ArrowForward />}
                          disabled={isSubmitting}
                          sx={{
                            fontWeight: 600,
                            backgroundColor: COLORS.secondary,
                            borderRadius: 2,
                            px: 4,
                            py: 1,
                            textTransform: 'none',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: `0 8px 25px rgba(59, 147, 148, 0.4)`,
                              backgroundColor: COLORS.secondary
                            },
                            transition: 'all 0.3s ease',
                            boxShadow: `0 4px 14px rgba(59, 147, 148, 0.3)`
                          }}
                        >
                          Next Question
                        </Button>
                      )}

                      {currentPage === examData.exam.questionList.length - 1 && (
                        <Button
                          data-testid="final-submit-btn"
                          onClick={handleNextOrSubmit}
                          variant="contained"
                          disabled={isSubmitting}
                          sx={{
                            fontWeight: 600,
                            backgroundColor: COLORS.secondary,
                            borderRadius: 2,
                            px: 4,
                            py: 1,
                            textTransform: 'none',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: `0 8px 25px rgba(59, 147, 148, 0.4)`,
                              backgroundColor: COLORS.secondary
                            },
                            transition: 'all 0.3s ease',
                            boxShadow: `0 4px 14px rgba(59, 147, 148, 0.3)`
                          }}
                        >
                          {isSubmitting ? "Submitting..." : "Submit Exam"}
                        </Button>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card
                elevation={0}
                sx={{
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 3,
                  backgroundColor: COLORS.surfaceElevated,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                  background: 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)'
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: COLORS.textPrimary,
                      fontWeight: 700,
                      mb: 3,
                      fontSize: '1.1rem'
                    }}
                  >
                    Instructions
                  </Typography>

                  <Box sx={{ space: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2.5 }}>
                      <CheckCircle sx={{ color: COLORS.success, fontSize: 22, mt: 0.25 }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.textPrimary, mb: 0.5 }}>
                          Answer selection
                        </Typography>
                        <Typography variant="body2" sx={{ color: COLORS.textSecondary, lineHeight: 1.5 }}>
                          Click any option to select your answer. You can change your selection anytime.
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2.5 }}>
                      <AccessTime sx={{ color: COLORS.warning, fontSize: 22, mt: 0.25 }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.textPrimary, mb: 0.5 }}>
                          Time management
                        </Typography>
                        <Typography variant="body2" sx={{ color: COLORS.textSecondary, lineHeight: 1.5 }}>
                          Keep an eye on the timer. The exam will auto-submit when time expires.
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2.5 }}>
                      <Flag sx={{ color: COLORS.primary, fontSize: 22, mt: 0.25 }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.textPrimary, mb: 0.5 }}>
                          Review questions
                        </Typography>
                        <Typography variant="body2" sx={{ color: COLORS.textSecondary, lineHeight: 1.5 }}>
                          Use "Mark for review" to flag questions you want to revisit later.
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <Circle sx={{ color: COLORS.secondary, fontSize: 22, mt: 0.25 }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.textPrimary, mb: 0.5 }}>
                          Navigation
                        </Typography>
                        <Typography variant="body2" sx={{ color: COLORS.textSecondary, lineHeight: 1.5 }}>
                          Use the question navigator to jump between questions quickly.
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{
                    mt: 4,
                    p: 3,
                    backgroundColor: `${COLORS.primary}08`,
                    borderRadius: 2,
                    border: `1px solid ${COLORS.primary}20`,
                    boxShadow: '0 4px 12px rgba(116, 110, 172, 0.1)'
                  }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: COLORS.primary, mb: 2, textAlign: 'center' }}>
                      Quick stats
                    </Typography>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                        gap: '16px',
                        alignItems: 'start',
                      }}
                    >
                      <Box sx={{ minWidth: 0, textAlign: 'center' }}>
                          <Typography
                            component="div"
                            sx={{
                              fontWeight: 800,
                              fontSize: 'clamp(1.25rem, 2.5vw, 2.125rem)',
                              lineHeight: 1.2,
                              color: COLORS.primary,
                            }}
                          >
                            {answeredCount}
                          </Typography>
                          <Typography
                            component="span"
                            sx={{
                              display: 'block',
                              color: COLORS.textSecondary,
                              fontWeight: 600,
                              lineHeight: 1.25,
                              whiteSpace: 'normal',
                              overflowWrap: 'anywhere',
                            }}
                          >
                            Answered
                          </Typography>
                      </Box>
                      <Box sx={{ minWidth: 0, textAlign: 'center' }}>
                          <Typography
                            component="div"
                            sx={{
                              fontWeight: 800,
                              fontSize: 'clamp(1.25rem, 2.5vw, 2.125rem)',
                              lineHeight: 1.2,
                              color: COLORS.warning,
                            }}
                          >
                            {markedCount}
                          </Typography>
                          <Typography
                            component="span"
                            sx={{
                              display: 'block',
                              color: COLORS.textSecondary,
                              fontWeight: 600,
                              lineHeight: 1.25,
                              whiteSpace: 'normal',
                              overflowWrap: 'anywhere',
                            }}
                          >
                            Marked
                          </Typography>
                      </Box>
                      <Box sx={{ minWidth: 0, textAlign: 'center' }}>
                          <Typography
                            component="div"
                            sx={{
                              fontWeight: 800,
                              fontSize: 'clamp(1.25rem, 2.5vw, 2.125rem)',
                              lineHeight: 1.2,
                              color: COLORS.textSecondary,
                            }}
                          >
                            {unansweredCount}
                          </Typography>
                          <Typography
                            component="span"
                            sx={{
                              display: 'block',
                              color: COLORS.textSecondary,
                              fontWeight: 600,
                              lineHeight: 1.25,
                              whiteSpace: 'normal',
                              overflowWrap: 'anywhere',
                            }}
                          >
                            Remaining
                          </Typography>
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <SubmitConfirmationDialog />

      <Dialog
        id="time-up-dialog"
        open={timeUpDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            textAlign: 'center',
            background: 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)'
          }
        }}
      >
        <DialogContent sx={{ p: 4 }}>
          <AccessTime sx={{ fontSize: 72, color: COLORS.warning, mb: 3 }} />
          <Typography variant="h4" fontWeight={800} sx={{ color: COLORS.textPrimary, mb: 2 }}>
            Time's up!
          </Typography>
          <Typography variant="body1" sx={{ color: COLORS.textSecondary, mb: 3, lineHeight: 1.6, fontSize: '1.1rem' }}>
            Your time for this Exam has ended. Your answers will be automatically submitted now.
          </Typography>
          <CircularProgress sx={{ color: COLORS.primary, mb: 2 }} />
          <Typography variant="caption" sx={{ color: COLORS.textSecondary, display: 'block' }}>
            Auto-submitting in 5 seconds...
          </Typography>
        </DialogContent>
      </Dialog>

    </>
  );
};

export default Question;
